import { GoLink, PageBanner, RiIcon, Section, SectionTitle } from '@longnan/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage } from '../useSiteData';
import {WithSiteData} from './common';
import type { SiteData } from '../useSiteData';

function Banner({ d }: { d: SiteData }) {
  const { t, pick } = useT();
  const img = findImage(d.imagery, 'convention');
  return (
    <PageBanner
      image={assetUrl(img.local)}
      imageAlt={pick(img.alt)}
      imageCredit={creditOf(img)}
      eyebrow={t('about.eyebrow')}
      title={pick(d.overview.identity.name)}
      lead={pick(d.company.slogan)}
    />
  );
}

function Intro({ d }: { d: SiteData }) {
  const { t, pick } = useT();
  return (
    <Section id="intro">
      <div className="grid gap-12 md:grid-cols-[1fr_1.4fr]">
        <SectionTitle id="intro" title={t('about.intro.title')} />
        <div className="space-y-5 text-base leading-relaxed text-body md:text-lg">
          {d.company.intro.map((p, i) => (
            <p key={i}>{pick(p)}</p>
          ))}
          <dl className="mt-10 grid gap-3 border-t border-line pt-6 text-sm">
            <div className="flex flex-wrap gap-x-3">
              <dt className="min-w-32 text-muted">{t('legal.label.name')}</dt>
              <dd className="text-body">{t('legal.name')}</dd>
            </div>
            <div className="flex flex-wrap gap-x-3">
              <dt className="min-w-32 text-muted">{t('legal.label.tax')}</dt>
              <dd className="tnum tracking-wide text-body">{t('legal.taxId')}</dd>
            </div>
            <div className="flex flex-wrap gap-x-3">
              <dt className="min-w-32 text-muted">{t('legal.label.address')}</dt>
              <dd className="text-body">{t('legal.address')}</dd>
            </div>
          </dl>
        </div>
      </div>
    </Section>
  );
}

function Values({ d }: { d: SiteData }) {
  const { t, pick } = useT();
  return (
    <Section id="values" tone="surface">
      <SectionTitle id="values" title={t('about.values.title')} />
      <ul className="mt-12 grid gap-4 md:grid-cols-3">
        {d.company.values.map((v) => (
          <li key={v.id} className="rounded-xl border border-line bg-bg p-8">
            <div className="text-sm font-semibold tracking-widest text-primary-light">{pick(v.title)}</div>
            <p className="mt-4 text-lg leading-relaxed text-body">{pick(v.body)}</p>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function History({ d }: { d: SiteData }) {
  const { t, pick } = useT();
  return (
    <Section id="history">
      <SectionTitle id="history" title={t('about.history.title')} lead={t('about.history.lead')} />
      <ol className="relative mt-12 border-l border-line-strong pl-8">
        {d.company.history.map((h, i) => (
          <li key={i} className="relative mb-10 last:mb-0">
            <span aria-hidden="true" className="absolute -left-[37px] top-1 h-4 w-4 rounded-full border-2 border-primary bg-bg" />
            <div className="flex flex-wrap items-center gap-3">
              <span className="tnum text-sm font-semibold text-body">{h.date}</span>
            </div>
            <div className="mt-2 text-lg font-semibold text-body">{pick(h.title)}</div>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted">{pick(h.body)}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function Structure({ d }: { d: SiteData }) {
  const { t, pick } = useT();
  return (
    <Section id="structure" tone="surface">
      <SectionTitle id="structure" title={t('about.structure.title')} lead={t('about.structure.lead')} />
      <div className="mt-12 rounded-xl border border-line bg-bg p-6 md:p-10">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="h-3 w-3 rounded-full bg-body" />
          <span className="text-base font-semibold text-body">{t('about.structure.root')}</span>
        </div>
        <ul className="mt-6 grid gap-3 border-l border-line-strong pl-6 sm:grid-cols-2 lg:grid-cols-3">
          {d.eco.subsidiaries.map((s) => (
            <li key={s.key} className="relative">
              <span aria-hidden="true" className="absolute -left-6 top-6 h-px w-6 bg-line-strong" />
              <a href={`#/ecosystem`} className="flex h-full flex-col rounded-lg border border-line p-4 hover:border-primary">
                <div className="flex items-center gap-3">
                  <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded bg-primary text-on-dark">
                    <RiIcon name={s.mark} className="text-base" />
                  </span>
                  <span className="text-sm font-semibold text-body">{pick(s.shortName)}</span>
                </div>
                <span className="mt-2 text-xs text-muted">{pick(s.positioning)}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}

function Closing() {
  const { t } = useT();
  return (
    <Section id="closing" tone="ink">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <p className="max-w-2xl text-xl leading-relaxed text-on-dark">{t('home.join.title')}</p>
        <GoLink to="/ecosystem" className="inline-flex rounded-md bg-bg px-6 py-3 text-sm font-semibold text-body hover:bg-surface">
          {t('home.hero.cta.ecosystem')}
        </GoLink>
      </div>
    </Section>
  );
}

export function About() {
  return (
    <WithSiteData>
      {(d) => (
        <>
          <Banner d={d} />
          <Intro d={d} />
          <Values d={d} />
          <History d={d} />
          <Structure d={d} />
          <Closing />
        </>
      )}
    </WithSiteData>
  );
}
